import React from "react"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import MyAlert from "@common-utils/MyAlert"
import { Link, useNavigate } from "react-router-dom"
import { ICreateConnectedUserNdfRequestModel } from "../ndf/core/_models"
import moment from "moment/moment"
import GlobalHelper from "@common-helpers/GlobalHelper"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_MOIS_LIST } from "@common-constants/*"
import { toast } from "react-toastify"
import MyCard from "@common-utils/MyCard"
import MySelectField from "@common-utils/fields/MySelectField"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { createConnectedUserDeclarationRequest, getConnectedUserDeclarationByDateRequest } from "../core/_requests"

const urlParams = new URLSearchParams(window.location.search)

const ConnectedUserCreateDeclarationForm = () => {
     const navigate = useNavigate()
     const urlParamsMois = urlParams.get("mois") as string | null
     const urlParamsAnnee = urlParams.get("annee") as string | null

     // Schema
     const schema = Yup.object().shape({
          mois: Yup.string().label(`"Mois"`).required(),
          annee: Yup.string().label(`"Année"`).required(),
     })

     // Init values
     const initialValues: ICreateConnectedUserNdfRequestModel = {
          mois: urlParamsMois || moment().format("MM"),
          annee: urlParamsAnnee || moment().format("YYYY"),
     }

     // Handle submit
     function handleSubmit(values: ICreateConnectedUserNdfRequestModel, helpers: FormikHelpers<ICreateConnectedUserNdfRequestModel>) {
          helpers.setStatus(null)

          getConnectedUserDeclarationByDateRequest(values.mois as string, values.annee as string).then(r => {
               if (!r.data) {
                    createConnectedUserDeclarationRequest({ mois: values.mois, annee: values.annee })
                         .then(r => {
                              helpers.setSubmitting(false)
                              navigate(`/personnel/mes-activites/declarations/${r.data.id}/details`, {
                                   state: {
                                        breadCrumbPageTitle: `${GlobalHelper.firstLetterUppercase(moment(`${values.annee}-${values.mois}-01`).format("MMMM YYYY"))}`,
                                   },
                              })
                         })
                         .catch((e: AxiosError) => {
                              const error: IHttpErrorResponseModel = e.response?.data

                              // Set form errors
                              if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                                   for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                              }

                              // Set form global status and notify user using a toast
                              helpers.setStatus(error?.detail)
                              toast.error(error?.detail, { autoClose: false })

                              // Stop submit loader
                              helpers.setSubmitting(false)
                         })
               } else {
                    navigate(`/personnel/mes-activites/declarations/${r.data.id}/details`, {
                         state: {
                              breadCrumbPageTitle: `${GlobalHelper.firstLetterUppercase(moment(`${values.annee}-${values.mois}-01`).format("MMMM YYYY"))}`,
                         },
                    })
               }
          })
     }

     const Header = () => {
          return (
               <div className="stepper stepper-links mb-5">
                    <div className="stepper-nav">
                         <div className={`stepper-item m-sm-5 m-4 current`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>Période</span>
                                   <span className={"d-block d-sm-none fas fa-calendar-plus"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>Arrêts</span>
                                   <span className={"d-block d-sm-none fas fa-file-medical"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>Congés</span>
                                   <span className={"d-block d-sm-none fas fa-umbrella-beach"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>CRA</span>
                                   <span className={"d-block d-sm-none fas fa-calendar-days"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>NDF</span>
                                   <span className={"d-block d-sm-none fas fa-file-invoice-dollar"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>TR</span>
                                   <span className={"d-block d-sm-none fas fas fa-utensils"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>

                         <div className={`stepper-item m-sm-5 m-4`}>
                              <h3 className="stepper-title">
                                   <span className={"d-none d-sm-block"}>Récap.</span>
                                   <span className={"d-block d-sm-none fas fa-list-alt"} style={{ fontSize: "1.5rem" }}></span>
                              </h3>
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <div className={"d-flex justify-content-center"}>
               <div className={"w-100 w-lg-75 w-xxl-65"}>
                    <MyCard>
                         <MyCard.Body>
                              <Header />
                              <h5 className={"mb-3"}>Choisissez une période</h5>
                              <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                                   {helpers => {
                                        console.log(helpers.errors)
                                        return (
                                             <Form noValidate autoComplete="off">
                                                  {helpers.status && (
                                                       <MyAlert type={"danger"} classNames={"mb-4"}>
                                                            {helpers.status}
                                                       </MyAlert>
                                                  )}

                                                  <div className="row mb-5">
                                                       <div className="col-sm-6 mb-sm-0 mb-3">
                                                            <MySelectField
                                                                 options={CONST_MOIS_LIST}
                                                                 name={"mois"}
                                                                 value={helpers.values.mois}
                                                                 isSearchable={false}
                                                                 onChange={value => helpers.setFieldValue("mois", value)}
                                                                 placeholder={"Mois"}
                                                                 isInvalid={!!helpers.errors.mois}
                                                            />
                                                            {helpers.errors.mois && <div className={"text-danger"}>{helpers.errors.mois}</div>}
                                                       </div>
                                                       <div className="col-sm-6">
                                                            <MySelectField
                                                                 options={[
                                                                      { label: moment().subtract(1, "year").format("YYYY"), value: moment().subtract(1, "year").format("YYYY") },
                                                                      {
                                                                           label: moment().format("YYYY"),
                                                                           value: moment().format("YYYY"),
                                                                      },
                                                                      {
                                                                           label: moment().add("1", "year").format("YYYY"),
                                                                           value: moment().add("1", "year").format("YYYY"),
                                                                      },
                                                                 ]}
                                                                 name={"annee"}
                                                                 isSearchable={false}
                                                                 value={helpers.values.annee}
                                                                 onChange={value => helpers.setFieldValue("annee", value)}
                                                                 placeholder={"Année"}
                                                                 isInvalid={!!helpers.errors.annee}
                                                            />
                                                            {helpers.errors.annee && <div className={"text-danger"}>{helpers.errors.annee}</div>}
                                                       </div>
                                                  </div>

                                                  {/* FOOTER */}
                                                  <div className="d-flex flex-stack">
                                                       <Link to="/personnel/mes-activites/declarations " className={`btn btn-sm btn-light-primary me-2`}>
                                                            <i className={"fas fa-list-dots align-middle me-1"} /> Retour aux déclarations
                                                       </Link>

                                                       <button type="submit" className="btn btn-md-lg btn-sm btn-primary" disabled={helpers.isSubmitting}>
                                                            {!helpers.isSubmitting ? (
                                                                 <>
                                                                      Suivant <i className={"fas fa-arrow-right align-middle ms-1"} />
                                                                 </>
                                                            ) : (
                                                                 <MySimpleSpinner size={"sm"} />
                                                            )}
                                                       </button>
                                                  </div>
                                             </Form>
                                        )
                                   }}
                              </Formik>
                         </MyCard.Body>
                    </MyCard>
               </div>
          </div>
     )
}

interface IFormikInitialValuesModel {
     mois?: string
     annee?: string
}

export default ConnectedUserCreateDeclarationForm
